import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useCookieConsentContext } from "@/hooks/useCookieConsent";
import { Cookie, Shield, BarChart3 } from "lucide-react";
import { Link } from "react-router-dom";

const CookiePreferencesModal = () => {
  const { 
    consent, 
    isPreferencesOpen, 
    closePreferences, 
    setConsent, 
    acceptAll, 
    rejectAll 
  } = useCookieConsentContext();

  const [analyticsEnabled, setAnalyticsEnabled] = useState(false);

  // Sync state with consent when modal opens
  useEffect(() => {
    if (isPreferencesOpen && consent) {
      setAnalyticsEnabled(consent.analytics);
    }
  }, [isPreferencesOpen, consent]);

  const handleSave = () => {
    setConsent({
      analytics: analyticsEnabled,
      functional: true, // Always true since Mapbox/reCAPTCHA are now essential
    });
    closePreferences();
  };

  const handleAcceptAll = () => {
    acceptAll();
    closePreferences();
  };

  const handleRejectAll = () => {
    rejectAll();
    closePreferences();
  };

  const formatDate = (timestamp: number) => {
    if (!timestamp) return null;
    return new Date(timestamp).toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <Dialog open={isPreferencesOpen} onOpenChange={closePreferences}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Cookie className="h-6 w-6 text-primary" />
            Gérer les préférences cookies
          </DialogTitle>
          <DialogDescription>
            Choisissez les cookies analytiques que vous acceptez. Les cookies essentiels sont nécessaires au fonctionnement du site.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Last consent date */}
          {consent?.timestamp && (
            <p className="text-sm text-muted-foreground">
              Dernier choix enregistré le {formatDate(consent.timestamp)}
            </p>
          )}

          {/* Cookie categories */}
          <div className="space-y-4">
            {/* Essential */}
            <div className="border border-border rounded-lg p-4">
              <div className="flex items-start gap-4">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Shield className="h-5 w-5 text-primary" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-foreground">Cookies essentiels</h4>
                    <Switch checked={true} disabled />
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    Ces cookies sont nécessaires au fonctionnement et à la sécurité du site. 
                    Ils ne peuvent pas être désactivés.
                  </p>
                  <div className="mt-3 text-xs text-muted-foreground space-y-1">
                    <p><strong>Préférences :</strong> Mémorisation de vos choix (cookies, sidebar)</p>
                    <p><strong>Google reCAPTCHA :</strong> Protection contre le spam et les robots sur les formulaires</p>
                    <p><strong>Mapbox :</strong> Affichage de la carte interactive des agences</p>
                  </div>
                  <p className="mt-2 text-xs text-muted-foreground italic">
                    Ces services sont considérés comme techniquement nécessaires conformément à l'article 82 de la directive ePrivacy.
                  </p>
                </div>
              </div>
            </div>

            {/* Analytics */}
            <div className="border border-border rounded-lg p-4">
              <div className="flex items-start gap-4">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <BarChart3 className="h-5 w-5 text-primary" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-foreground">Cookies analytiques</h4>
                    <Switch
                      checked={analyticsEnabled}
                      onCheckedChange={setAnalyticsEnabled}
                    />
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    Ces cookies nous aident à comprendre comment les visiteurs utilisent notre site, 
                    afin de l'améliorer. Les données sont anonymisées et hébergées en Europe.
                  </p>
                  <div className="mt-2 text-xs text-muted-foreground">
                    <strong>Service utilisé :</strong> Matomo (alternative éthique à Google Analytics, hébergé en UE)
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Links to legal pages */}
          <div className="flex flex-wrap gap-4 text-sm">
            <Link to="/cookies" className="text-primary hover:underline" onClick={closePreferences}>
              Politique des cookies
            </Link>
            <Link to="/politique-confidentialite" className="text-primary hover:underline" onClick={closePreferences}>
              Politique de confidentialité
            </Link>
            <Link to="/mentions-legales" className="text-primary hover:underline" onClick={closePreferences}>
              Mentions légales
            </Link>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 justify-end pt-4 border-t border-border">
          <Button variant="outline" onClick={handleRejectAll}>
            Refuser les analytiques
          </Button>
          <Button variant="outline" onClick={handleAcceptAll}>
            Tout accepter
          </Button>
          <Button onClick={handleSave} className="bg-primary hover:bg-primary/90">
            Enregistrer mes choix
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CookiePreferencesModal;
